import { action, computed, makeObservable, observable, observableRef } from 'mobx';

import { StoreNode } from '../../store';
import { Store } from '../../store/store';
import {
  GuidedTrainingDomSync,
  clamp,
  getGuidedTrainingTargetRect,
  guidedTrainingTargetSelector,
  hasVisibleGuidedTrainingLibrary,
  type GuidedTrainingCoachPosition,
  type GuidedTrainingRect,
} from '../guidedTraining/guidedTrainingCore';

type CoachPosition = GuidedTrainingCoachPosition;

type CreateFirstLibraryGuidedTrainingRect = GuidedTrainingRect;

const targetId = 'sidebar-create-library';
const targetSelector = guidedTrainingTargetSelector(targetId);
const activeBodyClass = 'create-first-library-guided-training-active';

const getTargetRect = (): CreateFirstLibraryGuidedTrainingRect | null =>
  getGuidedTrainingTargetRect(targetId);

const getCoachPosition = (rect: CreateFirstLibraryGuidedTrainingRect | null): CoachPosition => {
  const width = Math.min(420, window.innerWidth - 32);
  const height = 230;

  if (!rect) {
    return {
      width,
      top: clamp(window.innerHeight * 0.35, 120, window.innerHeight - height - 24),
      left: 24,
    };
  }

  const rightLeft = rect.left + rect.width + 18;
  const canFitRight = rightLeft + width + 16 < window.innerWidth;
  const left = canFitRight
    ? rightLeft
    : clamp(rect.left + rect.width / 2 - width / 2, 16, window.innerWidth - width - 16);

  return {
    width,
    top: clamp(rect.top - height / 2 + rect.height / 2, 24, window.innerHeight - height - 24),
    left,
  };
};

export class CreateFirstLibraryGuidedTrainingState
  extends StoreNode {

  readonly nodeType = 'CreateFirstLibraryGuidedTraining';

  constructor(store: Store) {
    super(store);
    makeObservable(this, {
      isVisible: observable,
      targetRect: observableRef,
      coachPosition: observableRef,
      shouldStart: computed,
      shouldBlockNextLevel: computed,
      handleSidebarTeamsLoaded: action,
      open: action,
      close: action,
      next: action,
      syncPosition: action,
    });
    this.domSync = new GuidedTrainingDomSync({
      syncPosition: this.syncPosition,
      activeBodyClass,
    });
  }

  isVisible = false;
  targetRect: CreateFirstLibraryGuidedTrainingRect | null = null;
  coachPosition: CoachPosition = { top: 120, left: 24, width: 420 };

  private readonly domSync: GuidedTrainingDomSync;

  get shouldStart(): boolean {
    const showOnboarding = this.store.storage.getLocal('user.showOnboarding');
    return !!showOnboarding
      && !!this.store.user?.hasPermission('CreateTeam')
      // an unverified user gets their library once they verify, so don't ask them to make one
      && this.store.user.emailVerified !== false
      && this.store.pageSidebar.isTeamListFetched
      && !hasVisibleGuidedTrainingLibrary(this.store.pageSidebar.teams);
  }

  get shouldBlockNextLevel(): boolean {
    const showOnboarding = this.store.storage.getLocal('user.showOnboarding');
    return this.isVisible
      || (
        !!showOnboarding
        && !!this.store.user?.hasPermission('CreateTeam')
        && this.store.user.emailVerified !== false
        && (
          !this.store.pageSidebar.isTeamListFetched
          || !hasVisibleGuidedTrainingLibrary(this.store.pageSidebar.teams)
        )
      );
  }

  handleSidebarTeamsLoaded() {
    if (this.shouldStart) {
      this.open();
      return;
    }

    this.close();
  }

  open() {
    this.isVisible = true;
    this.domSync.bindListeners();
    this.domSync.schedulePositionSync();
  }

  close = () => {
    this.isVisible = false;
    this.targetRect = null;
    this.domSync.unbindListeners();
    this.domSync.cancelPositionSync();
  }

  next = () => {
    const node = document.querySelector<HTMLElement>(targetSelector);
    this.close();
    node?.click();
  }

  syncPosition = () => {
    const rect = getTargetRect();
    this.targetRect = rect;
    this.coachPosition = getCoachPosition(rect);
  }

}
